import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Globe2,
  Mail,
  Mic2,
  PlayCircle,
  ShieldCheck,
} from 'lucide-react'
import BetaCommandCenter, { type CommandCenterSignal } from '../../components/dashboard/BetaCommandCenter'
import MarketingLayout from '../../components/layout/MarketingLayout'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { trackHeroConversion, useHeroExperiment } from '../../hooks/useHeroExperiment'
import { salsaAnalytics } from '../../services/salsaAnalytics'

const images = {
  hero: '/images/onzoe-hero.png',
  access: '/images/login.png',
}

const carouselImages = {
  dataHub: '/images/legacy-graffiti-hero.webp',
  access: '/images/legacy-graffiti-login.webp',
  identity: '/images/legacy-graffiti-register.webp',
  leadTracking: '/images/legacy-graffiti-lead-tracking.webp',
}

const heroStats = [
  { value: 'Voice', label: 'answer in the moment' },
  { value: 'Tours', label: 'show the real product' },
  { value: 'Proof', label: 'track the outcome' },
]

const signalCards: CommandCenterSignal[] = [
  {
    title: 'Website agent',
    value: 'Installed',
    detail: 'We configure one focused agent around the questions and follow-ups that matter to your business.',
    tone: 'cyan',
  },
  {
    title: 'Lead alerts',
    value: 'Immediate',
    detail: 'Every consented lead reaches the account owner with the source page and captured context.',
    tone: 'amber',
  },
  {
    title: 'Proof report',
    value: 'Weekly',
    detail: 'Real visitors, interactions, conversations, and leads arrive in an honest weekly summary.',
    tone: 'white',
  },
]

const commandRows = [
  { label: 'Visitor asks a buying question', status: 'Answer + qualify', value: 'Intent' },
  { label: 'Visitor requests follow-up', status: 'Capture with consent', value: 'Lead' },
  { label: 'Owner reviews weekly report', status: 'Measure outcome', value: 'Proof' },
]

const productTabs = [
  {
    id: 'agent-builder',
    label: 'Agent builder',
    eyebrow: 'Agent builder',
    title: 'Shape one focused website voice agent.',
    body: 'Start with the real business use case, not a generic chatbot. Onzoe captures the agent name, prompt, business context, voice choice, and lead fields that define the first pilot.',
    image: carouselImages.identity,
    imageAlt: 'Neon circuit-person artwork glowing among graffiti-marked forest trees',
    accent: 'text-[#20D9FF]',
    proof: [
      'Business type, prompt, and behavior settings',
      'Lead fields for email, name, and phone',
      'Selected agent flows into widget setup',
      'Dashboard reads the live account configuration',
    ],
    cards: [
      { title: 'Configure', body: 'Set the greeting, qualification behavior, and voice tone for one website job.' },
      { title: 'Attach', body: 'Pair the agent with a real widget deployment and domain.' },
      { title: 'Review', body: 'See the selected agent reflected immediately in the customer dashboard.' },
    ],
    tech: 'Stored in the marketing service account tables as voice-agent configuration, then reused by widget config and dashboard APIs.',
  },
  {
    id: 'voice-widget',
    label: 'Voice widget',
    eyebrow: 'Voice widget',
    title: 'Add a spoken interface to the website.',
    body: 'The customer installs a website widget that can answer common questions, prompt for the next contact step, and use the configured voice provider instead of pretending a human is present.',
    image: carouselImages.dataHub,
    imageAlt: 'Cyberpunk data hub glowing inside a graffiti-covered industrial space',
    accent: 'text-[#FFB454]',
    proof: [
      'Public widget config by widget key',
      'Chat and voice session events',
      'Configurable voice and prompt settings',
      'Fallback copy when voice is not configured',
    ],
    cards: [
      { title: 'Load', body: 'The browser loader fetches widget and agent settings from Onzoe.' },
      { title: 'Speak', body: 'Voice synthesis proxies through the backend using the configured provider.' },
      { title: 'Capture', body: 'The widget asks for useful contact details when a visitor shows intent.' },
    ],
    tech: 'Uses /api/widget/config/:widgetKey and /api/widget/voice/synthesize, backed by configured TTS providers such as Cartesia, OpenAI, or ElevenLabs.',
  },
  {
    id: 'widget-install',
    label: 'Widget install',
    eyebrow: 'Widget install',
    title: 'Give customers real code they can ship.',
    body: 'The install step should not be an abstract deploy button. Onzoe gives the account a widget key, domain context, and a copyable loader script for the site page being tested.',
    image: carouselImages.access,
    imageAlt: 'Neon lock and key glowing against a graffiti-covered wall',
    accent: 'text-[#FFB454]',
    proof: [
      'Copyable loader script',
      'Widget key identifies the deployment',
      'Domain and agent pairing stay visible',
      'Install guide lives inside the dashboard',
    ],
    cards: [
      { title: 'Create', body: 'Create or reuse the selected agent widget deployment.' },
      { title: 'Copy', body: 'Copy the script with the current widget key and public loader URL.' },
      { title: 'Verify', body: 'Return to the widgets page to review activity after the snippet is live.' },
    ],
    tech: 'Install code is generated from the real widget deployment record and points at the Onzoe widget loader on onzoe.com.',
  },
  {
    id: 'lead-tracking',
    label: 'Lead tracking',
    eyebrow: 'Lead tracking',
    title: 'Track contact intent from the widget to the dashboard.',
    body: 'Lead Tracking is the evidence layer. Onzoe records widget activity, voice starts, messages, and submitted contact details so a team can see whether the website agent is creating follow-up opportunities.',
    image: carouselImages.leadTracking,
    imageAlt: 'Neon tracking paths passing through lead checkpoints and converging on a qualified contact beacon',
    accent: 'text-[#20D9FF]',
    proof: [
      'view, open, message, voice_session, and lead events',
      'Lead details saved with session and widget context',
      'Dashboard rollups for visitors, interactions, leads, and conversion',
      'Salsa analytics SDK can stream marketing-site engagement',
    ],
    cards: [
      { title: '/api/widget/events', body: 'Receives browser widget activity tied to widget key and session id.' },
      { title: '/api/widget/session/:id/lead', body: 'Stores captured email, name, phone, and context for review.' },
      { title: '/api/widgets/:id/results', body: 'Rolls Postgres event data into dashboard metrics for the account.' },
    ],
    tech: 'Uses the public widget event endpoints, Postgres-backed widget_events and voice session records, account-scoped dashboard APIs, and the Salsa analytics SDK where marketing analytics are enabled.',
  },
]

const platformPillars = [
  {
    title: 'Website voice widget',
    body: 'Put a spoken assistant on the site without building a custom front end.',
    icon: Globe2,
  },
  {
    title: 'First-touch lead capture',
    body: 'Ask simple qualifying questions and collect contact details for human follow-up.',
    icon: Mic2,
  },
  {
    title: 'Basic activity reporting',
    body: 'See deployments, interactions, estimated leads, and conversion rate where data is available.',
    icon: BarChart3,
  },
  {
    title: 'Install confidence',
    body: 'Keep the setup path concrete with widget IDs, API keys, and clear install snippets.',
    icon: ShieldCheck,
  },
]

export default function HomePage() {
  const [activeProductIndex, setActiveProductIndex] = useState(0)
  const [isTourVideoOpen, setIsTourVideoOpen] = useState(false)
  const videoMilestones = useRef(new Set<number>())
  const prefersReducedMotion = useReducedMotion()
  const heroAssignment = useHeroExperiment()
  const activeProductTab = productTabs[activeProductIndex] ?? productTabs[0]

  const trackPilotCta = (placement: string) => {
    trackHeroConversion(heroAssignment, 'apply_for_founding_pilot')
    salsaAnalytics.track('pilot_cta_clicked', {
      placement,
      offer: 'invite_only_early_access',
      destination: '/beta',
    })
  }

  const setTourVideoOpen = (open: boolean) => {
    setIsTourVideoOpen(open)
    salsaAnalytics.track(open ? 'tour_demo_video_opened' : 'tour_demo_video_closed', {
      placement: 'homepage_hero_secondary',
      asset: 'onzoe_tours_launch',
    })
  }

  const trackVideoProgress = (video: HTMLVideoElement) => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) return
    const progress = video.currentTime / video.duration
    ;[0.25, 0.5, 0.75].forEach((milestone) => {
      if (progress < milestone || videoMilestones.current.has(milestone)) return
      videoMilestones.current.add(milestone)
      salsaAnalytics.track('tour_demo_video_progress', {
        asset: 'onzoe_tours_launch',
        percent: milestone * 100,
      })
    })
  }

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined
    }

    const timer = window.setInterval(() => {
      setActiveProductIndex((current) => (current + 1) % productTabs.length)
    }, 5600)

    return () => window.clearInterval(timer)
  }, [prefersReducedMotion])

  return (
    <MarketingLayout>
      <div className="bg-[#F5F7FA] text-[#05070D] dark:bg-[#05070D] dark:text-white">
        <section className="relative overflow-hidden border-b border-[#20D9FF]/20">
          <div className="absolute inset-0">
            <img
              src={images.hero}
              alt="Onzoe Living Signal waveform glowing across a dark field"
              className="h-full w-full object-cover opacity-72"
            />
            <div className="absolute inset-0 bg-[#F5F7FA]/78 dark:bg-[#05070D]/70" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,#F5F7FA_0%,rgba(245,247,250,0.9)_36%,rgba(245,247,250,0.34)_100%)] dark:bg-[linear-gradient(90deg,#05070D_0%,rgba(5,7,13,0.88)_36%,rgba(5,7,13,0.34)_100%)]" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(180deg,rgba(245,247,250,0)_0%,#F5F7FA_82%)] dark:bg-[linear-gradient(180deg,rgba(5,7,13,0)_0%,#05070D_82%)]" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
            <div className="grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
              <div>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFB454]"
                >
                  VOICE · TOURS · PROOF
                </motion.p>
                <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.04 }}
                  className="mt-5 max-w-4xl text-4xl font-semibold leading-[0.95] sm:text-6xl lg:text-7xl"
                >
                  Make intelligence feel alive.
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="mt-5 max-w-2xl text-base leading-7 text-[#4C5965] dark:text-white/78 sm:mt-7 sm:text-lg sm:leading-8"
                >
                  Onzoe turns product knowledge into voice agents and narrated experiences that guide customers,
                  capture qualified interest, and show what happens next.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 }}
                  className="mt-7 flex flex-wrap gap-3 sm:mt-9"
                >
                  <Link
                    to="/beta"
                    onClick={() => trackPilotCta('homepage_hero_primary')}
                    className="cta-button inline-flex items-center justify-center rounded-md bg-[#FFB454] px-6 py-3 text-sm font-bold text-[#05070D] transition-colors hover:bg-[#ffc579]"
                  >
                    Request an Onzoe invite
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      trackHeroConversion(heroAssignment, 'see_proof_plan')
                      salsaAnalytics.track('pilot_cta_clicked', {
                        placement: 'homepage_hero_secondary',
                        offer: 'invite_only_early_access',
                        destination: 'tour_demo_video',
                      })
                      setTourVideoOpen(true)
                    }}
                    className="inline-flex items-center justify-center rounded-md border border-[#05070D]/18 dark:border-white/18 bg-[#05070D]/6 dark:bg-white/8 px-6 py-3 text-sm font-bold text-[#05070D] dark:text-white backdrop-blur transition-colors hover:bg-[#05070D]/14 dark:hover:bg-white/14"
                  >
                    <PlayCircle className="mr-2 h-4 w-4" />
                    See the living signal
                  </button>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.14 }}
                  className="mt-4 text-sm font-medium text-[#4C5965] dark:text-white/68"
                >
                  Invite only · Real product evidence · Results reported honestly
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.16 }}
                  className="mt-7 grid max-w-2xl grid-cols-3 gap-2 sm:mt-10 sm:gap-3"
                >
                  {heroStats.map((stat) => (
                    <div key={stat.label} className="border-l border-[#20D9FF]/35 bg-white/70 dark:bg-black/28 px-2 py-3 backdrop-blur sm:px-4">
                      <p className="text-base font-semibold leading-tight text-[#05070D] dark:text-white sm:text-xl">{stat.value}</p>
                      <p className="mt-1 text-[10px] uppercase leading-4 tracking-[0.12em] text-[#4C5965] dark:text-white/52 sm:text-xs sm:tracking-[0.16em]">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.16 }}
                className="hidden lg:block"
              >
                <BetaCommandCenter
                  eyebrow="The living signal"
                  subtitle="One experience from voice to outcome"
                  primaryTitle="Visitor requested follow-up"
                  primaryDescription="The agent answered the question, captured consent, alerted the owner, and added the result to the weekly report."
                  rows={commandRows}
                  signals={signalCards}
                />
              </motion.div>
            </div>
          </div>
        </section>

        <section
          id="product-experience"
          aria-label="Onzoe product experience carousel"
          className="border-y border-[#20D9FF]/20 bg-[#F5F7FA] text-[#05070D] dark:bg-[#05070D] dark:text-white"
        >
          <div className="border-b border-[#05070D]/10 bg-[#05070D] px-4 py-3 dark:border-white/10 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-7xl gap-2 sm:grid-cols-4">
              {productTabs.map((tab, index) => {
                const isActive = index === activeProductIndex

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveProductIndex(index)}
                    className={`relative min-h-12 overflow-hidden px-3 py-3 text-left text-xs font-bold uppercase tracking-[0.22em] transition-colors ${
                      isActive ? tab.accent : 'text-white/46 hover:text-white'
                    }`}
                    aria-pressed={isActive}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="product-tab-surface"
                        className="absolute inset-0 bg-white/[0.055]"
                        transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
                      />
                    )}
                    <span className="relative">{tab.label}</span>
                    {isActive && (
                      <motion.span
                        key={tab.id}
                        className="absolute inset-x-3 bottom-1 h-px bg-[#20D9FF]"
                        initial={{ scaleX: 0, transformOrigin: '0% 50%' }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 5.4, ease: 'linear' }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProductTab.id}
                initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -18, filter: 'blur(6px)' }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-stretch"
              >
                <div className="flex flex-col justify-between">
                  <div>
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.34, delay: 0.05 }}
                      className={`text-xs font-bold uppercase tracking-[0.24em] ${activeProductTab.accent}`}
                    >
                      {activeProductTab.eyebrow}
                    </motion.p>
                    <motion.h2
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.44, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                      className="mt-4 max-w-2xl text-4xl font-semibold leading-tight sm:text-5xl"
                    >
                      {activeProductTab.title}
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.44, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
                      className="mt-6 max-w-xl text-base leading-8 text-[#4C5965] dark:text-white/68"
                    >
                      {activeProductTab.body}
                    </motion.p>
                  </div>

                  <div className="mt-10 grid gap-4 sm:grid-cols-2">
                    {activeProductTab.proof.map((item, index) => (
                      <motion.div
                        key={item}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.36, delay: 0.22 + index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                        className="flex items-start gap-3 border-t border-[#05070D]/12 pt-4 dark:border-white/12"
                      >
                        <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#20D9FF] dark:text-[#FFB454]" />
                        <p className="text-sm leading-6 text-[#4C5965] dark:text-white/68">{item}</p>
                      </motion.div>
                    ))}
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.42, delay: 0.48, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 border-l border-[#20D9FF]/45 bg-white/70 px-5 py-4 text-sm leading-7 text-[#4C5965] dark:bg-white/[0.045] dark:text-white/68"
                  >
                    <span className="font-semibold text-[#05070D] dark:text-white">Technology: </span>
                    {activeProductTab.tech}
                  </motion.div>
                </div>

                <div className="overflow-hidden border border-[#05070D]/12 bg-[#05070D] dark:border-white/12">
                  <div className="relative h-72 overflow-hidden sm:h-96">
                    <motion.img
                      key={activeProductTab.image}
                      src={activeProductTab.image}
                      alt={activeProductTab.imageAlt}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                      initial={{ scale: 1.08, opacity: 0.45 }}
                      animate={{ scale: 1, opacity: 0.82 }}
                      exit={{ scale: 1.04, opacity: 0 }}
                      transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,6,7,0.05),rgba(5,6,7,0.78))]" />
                    <motion.div
                      className="absolute left-6 top-6 border border-[#20D9FF]/35 bg-black/42 px-4 py-3 text-white backdrop-blur"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.42, delay: 0.22 }}
                    >
                      <p className="text-xs uppercase tracking-[0.22em] text-[#20D9FF]">{activeProductTab.label}</p>
                      <p className="mt-2 text-sm text-white/72">Onzoe pilot path</p>
                    </motion.div>
                  </div>

                  <div className="grid gap-px bg-white/10 sm:grid-cols-3">
                    {activeProductTab.cards.map((item, index) => (
                      <motion.div
                        key={item.title}
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.42, delay: 0.28 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                        className="bg-[#05070D] p-5 text-white"
                      >
                        <p className={`text-sm font-semibold ${activeProductTab.accent}`}>{String(index + 1).padStart(2, '0')}</p>
                        <p className="mt-5 font-semibold">{item.title}</p>
                        <p className="mt-3 text-sm leading-6 text-white/62">{item.body}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </section>

        <section id="pilot-proof" className="bg-[#F5F7FA] py-20 dark:bg-[#05070D]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#FFB454]">Proof before promises</p>
                <h2 className="mt-4 max-w-xl text-4xl font-semibold leading-tight sm:text-5xl">
                  Know whether the website agent created a real follow-up opportunity.
                </h2>
                <p className="mt-6 max-w-xl text-base leading-8 text-[#4C5965] dark:text-white/66">
                  You receive immediate lead alerts, account-scoped event data, and a share-safe report built from
                  production activity. Sample data stays labeled; customer results are never invented.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {platformPillars.map((pillar) => (
                  <div key={pillar.title} className="border border-[#05070D]/10 dark:border-white/10 bg-white/78 dark:bg-white/[0.045] p-6">
                    <pillar.icon className="h-5 w-5 text-[#20D9FF]" />
                    <p className="mt-5 text-lg font-semibold text-[#05070D] dark:text-white">{pillar.title}</p>
                    <p className="mt-3 text-sm leading-7 text-[#4C5965] dark:text-white/62">{pillar.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 grid gap-6 border border-[#05070D]/10 bg-[#19232D] p-4 dark:border-white/10 lg:grid-cols-[1.08fr_0.92fr]">
              <div className="border border-[#05070D]/10 dark:border-white/10 bg-white/80 dark:bg-black/22 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.22em] text-[#FFB454]">Lead board</p>
                    <p className="mt-2 text-2xl font-semibold text-[#05070D] dark:text-white">Illustrative proof report</p>
                  </div>
                  <p className="border border-[#FFB454]/35 px-3 py-2 text-sm font-semibold text-[#FFB454]">Clearly labeled sample</p>
                </div>

                <div className="mt-8 grid gap-3 md:grid-cols-3">
                  {['Visitors', 'Interactions', 'Leads'].map((stage, index) => (
                    <div key={stage} className="border-t border-[#05070D]/10 dark:border-white/10 pt-4">
                      <p className="text-xs uppercase tracking-[0.18em] text-[#4C5965] dark:text-white/42">{stage}</p>
                      <p className="mt-3 text-3xl font-semibold text-[#05070D] dark:text-white">{[238, 41, 8][index]}</p>
                      <p className="mt-2 text-sm text-[#4C5965] dark:text-white/56">{['sample visits', 'widget starts', 'contact captures'][index]}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative min-h-[320px] overflow-hidden border border-[#05070D]/10 dark:border-white/10">
                <img
                  src={images.access}
                  alt="Onzoe Living Signal illuminating the product access layer"
                  className="absolute inset-0 h-full w-full object-cover opacity-68"
                />
                <div className="absolute inset-0 bg-[#F5F7FA]/64 dark:bg-[#05070D]/58" />
                <div className="relative flex h-full flex-col justify-end p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#FFB454]">Access layer</p>
                  <h3 className="mt-4 max-w-sm text-3xl font-semibold leading-tight text-[#05070D] dark:text-white">
                    After signup, the dashboard keeps setup focused.
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-7 text-[#4C5965] dark:text-white/68">
                    The account flow points the customer toward agent setup, widget installation, and lead review.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden border-t border-[#20D9FF]/20 bg-[#F5F7FA] dark:bg-[#05070D]">
          <div className="absolute inset-0">
            <img
              src={images.hero}
              alt="Onzoe Living Signal returning behind the invitation"
              className="h-full w-full object-cover opacity-32"
            />
            <div className="absolute inset-0 bg-[#F5F7FA]/84 dark:bg-[#05070D]/82" />
          </div>
          <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_auto] lg:items-center lg:px-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#20D9FF]">Private early access</p>
              <h2 className="mt-4 max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl">
                Give your product a voice—and prove what it changes.
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-[#4C5965] dark:text-white/68">
                Onzoe is invite-only while we work closely with early customers. Build an agent, guide the real product, and connect every experience to a measurable outcome.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/beta"
                onClick={() => trackPilotCta('homepage_final_primary')}
                className="inline-flex items-center justify-center rounded-md bg-[#FFB454] px-6 py-3 text-sm font-bold text-[#05070D] transition-colors hover:bg-[#ffc579]"
              >
                Request an invite
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                onClick={() => salsaAnalytics.track('sales_contact_clicked', { placement: 'homepage_final_secondary' })}
                className="inline-flex items-center justify-center rounded-md border border-[#05070D]/18 dark:border-white/18 px-6 py-3 text-sm font-bold text-[#05070D] dark:text-white transition-colors hover:bg-[#05070D]/10 dark:hover:bg-white/10"
              >
                <Mail className="mr-2 h-4 w-4" />
                Talk to sales
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Dialog open={isTourVideoOpen} onOpenChange={setTourVideoOpen}>
        <DialogContent className="w-[min(1120px,calc(100vw-2rem))] max-w-none gap-0 overflow-hidden border border-[#20D9FF]/35 bg-[#05070D] p-0 text-white shadow-[0_28px_100px_rgba(0,0,0,0.72)] [&>button]:right-4 [&>button]:top-4 [&>button]:z-20 [&>button]:rounded-full [&>button]:bg-black/65 [&>button]:p-2 [&>button]:text-white">
          <div className="border-b border-white/10 px-5 py-4 pr-16 sm:px-7 sm:py-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#20D9FF]">Onzoe Tour demo</p>
            <DialogTitle className="mt-2 text-2xl font-semibold sm:text-3xl">The product becomes the proof.</DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-6 text-white/66">
              Onzoe narrates the complete Voice → Tours → Proof experience.
            </DialogDescription>
          </div>
          <div className="bg-black">
            <video
              className="aspect-video max-h-[68vh] w-full bg-black object-contain"
              controls
              autoPlay
              playsInline
              preload="metadata"
              poster="/brand/onzoe-og.png"
              onPlay={() => salsaAnalytics.track('tour_demo_video_played', { asset: 'onzoe_tours_launch' })}
              onTimeUpdate={(event) => trackVideoProgress(event.currentTarget)}
              onEnded={() => salsaAnalytics.track('tour_demo_video_completed', { asset: 'onzoe_tours_launch' })}
            >
              <source src="/videos/onzoe-tour-demo.mp4" type="video/mp4" />
              <track kind="captions" src="/videos/onzoe-tour-demo.vtt" srcLang="en" label="English" default />
              Your browser does not support embedded video.
            </video>
          </div>
          <div className="flex flex-col gap-3 border-t border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
            <p className="text-sm text-white/60">Prefer to interact with the product itself?</p>
            <a
              href="/demo/tour"
              onClick={() => salsaAnalytics.track('live_tour_demo_clicked', { placement: 'video_modal' })}
              className="inline-flex items-center justify-center rounded-md bg-[#20D9FF] px-5 py-2.5 text-sm font-bold text-[#05070D] transition-colors hover:bg-[#69e8ff]"
            >
              Start the live narrated Tour
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </MarketingLayout>
  )
}
